import { useSyncExternalStore } from 'react';
import { Capacitor } from '@capacitor/core';
import { Network } from '@capacitor/network';
import {
  EFFECTIVE_CONNECTIVITY,
  getEffectiveConnectivity,
  isEffectivelyDegraded,
  isEffectivelyUnreachable,
  isPrefetchAllowed,
  isNetworkAssociated,
  setNetworkAssociationHint,
  subscribeEffectiveConnectivityInputs,
  subscribeNetworkAssociationHint,
} from '../lib/network/effectiveConnectivity';
import { getRecentFetchStats } from '../lib/network/fetchOutcomes';
import { getRealtimeHealth } from '../lib/realtime/subscriptionHealth';

const POOR_CONNECTION_TYPES = new Set(['cellular', '2g', '3g', 'slow-2g']);

/** Stable server snapshot — must not allocate a new object per call (React #185). */
const SERVER_CONNECTION_META = { connectionType: 'unknown', connectionQuality: 'unknown' };

let connectionType = 'unknown';
let connectionQuality = 'unknown';
let connectionMetaSnapshot = { connectionType: 'unknown', connectionQuality: 'unknown' };
const connectionMetaListeners = new Set();

function emitConnectionMetaChange() {
  connectionMetaListeners.forEach((listener) => listener());
}

function setConnectionMeta(type, quality) {
  const nextType = type ?? 'unknown';
  const nextQuality = quality ?? 'unknown';
  if (connectionType === nextType && connectionQuality === nextQuality) return;
  connectionType = nextType;
  connectionQuality = nextQuality;
  connectionMetaSnapshot = { connectionType, connectionQuality };
  emitConnectionMetaChange();
}

function subscribeConnectionMeta(listener) {
  connectionMetaListeners.add(listener);
  return () => connectionMetaListeners.delete(listener);
}

function getConnectionMetaSnapshot() {
  return connectionMetaSnapshot;
}

let networkInitStarted = false;

function initNetworkQualityTracking() {
  if (networkInitStarted || typeof window === 'undefined') return;
  networkInitStarted = true;

  if (Capacitor.isNativePlatform()) {
    Network.getStatus()
      .then((status) => {
        setNetworkAssociationHint(status.connected);
        setConnectionMeta(status.connectionType, status.connectionType);
      })
      .catch(() => {
        setNetworkAssociationHint(true);
        setConnectionMeta('unknown', 'unknown');
      });

    Network.addListener('networkStatusChange', (status) => {
      setNetworkAssociationHint(status.connected);
      setConnectionMeta(status.connectionType, status.connectionType);
    });
    return;
  }

  const nav = typeof navigator !== 'undefined' ? navigator : null;
  const connection = nav?.connection || nav?.mozConnection || nav?.webkitConnection;

  const syncFromNavigator = () => {
    setNetworkAssociationHint(typeof navigator !== 'undefined' ? navigator.onLine : true);
    const type = connection?.type || connection?.effectiveType || 'unknown';
    const quality = connection?.effectiveType || type;
    setConnectionMeta(type, quality);
  };

  syncFromNavigator();
  window.addEventListener('online', syncFromNavigator);
  window.addEventListener('offline', syncFromNavigator);
  connection?.addEventListener?.('change', syncFromNavigator);
}

function getEffectiveConnectivitySnapshot() {
  return getEffectiveConnectivity();
}

/**
 * Shared connectivity for prefetch gating, offline UI, and polling.
 * Uses fetch outcomes + realtime health; Network plugin is a weak hint only.
 */
export function useNetworkStatus() {
  initNetworkQualityTracking();

  const effectiveConnectivity = useSyncExternalStore(
    subscribeEffectiveConnectivityInputs,
    getEffectiveConnectivitySnapshot,
    () => EFFECTIVE_CONNECTIVITY.UNKNOWN,
  );

  const isNetworkAssociatedFlag = useSyncExternalStore(
    subscribeNetworkAssociationHint,
    isNetworkAssociated,
    () => true,
  );

  const { connectionType: type, connectionQuality: quality } = useSyncExternalStore(
    subscribeConnectionMeta,
    getConnectionMetaSnapshot,
    () => SERVER_CONNECTION_META,
  );

  const isCellular =
    type === 'cellular' || POOR_CONNECTION_TYPES.has(String(quality).toLowerCase());

  const isReachable = effectiveConnectivity === EFFECTIVE_CONNECTIVITY.REACHABLE;
  const isDegraded =
    effectiveConnectivity === EFFECTIVE_CONNECTIVITY.DEGRADED ||
    (effectiveConnectivity === EFFECTIVE_CONNECTIVITY.UNKNOWN && isCellular);
  const isUnreachable = effectiveConnectivity === EFFECTIVE_CONNECTIVITY.UNREACHABLE;

  const fetchStats = getRecentFetchStats();
  const realtimeHealth = getRealtimeHealth();

  const prefetchAllowed = isPrefetchAllowed();

  return {
    /** @deprecated Weak signal — radio/interface associated, not reachability. */
    isOnline: isNetworkAssociatedFlag,
    isNetworkAssociated: isNetworkAssociatedFlag,
    effectiveConnectivity,
    isEffectivelyOnline: isReachable,
    isEffectivelyDegraded: isDegraded,
    isEffectivelyUnreachable: isUnreachable,
    connectionType: type,
    connectionQuality: quality,
    isCellular,
    isPoorConnection: isDegraded || isUnreachable,
    prefetchAllowed,
    fetchStats,
    realtimeHealth,
  };
}

export {
  EFFECTIVE_CONNECTIVITY,
  getEffectiveConnectivity,
  isEffectivelyDegraded,
  isEffectivelyUnreachable,
  isPrefetchAllowed,
};
